import {
  elementOutsideSlideClone,
  firstOutsideSlideClone,
} from './portal_helpers.js'

export function isEditingBuilderDoc() {
  return !!document.querySelector('[data-builder-doc-editing=true]')
}

export function isPreviewingBuilderDoc() {
  return !!document.querySelector('[data-builder-doc-previewing=true]')
}

export const getPreviewIframe = () =>
  document.querySelector(
    '.js-preview-container[data-preview-id="history"]:not(.hidden) iframe.js-preview-frame'
  ) ||
  document.querySelector(
    '.js-preview-container:not(.hidden) iframe.js-preview-frame'
  ) ||
  document.querySelector('iframe.js-preview-frame')

export const isPreviewIframe = () =>
  window.frameElement?.classList?.contains('js-preview-frame')

// Null when there is no preview iframe yet. It has to be null rather than a throw: callers
// reasonably write `getPreviewIframeDoc()?.body`, and a throw *inside* this call happens
// before that `?.` can do anything — which is how SIMPLERO-13HW crashed a guarded call site
// (skeleton_component_controller#updateBreakpointClasses) during connect(), on a style guide
// whose iframe had not been parsed yet.
export const getPreviewIframeDoc = () =>
  getPreviewIframe()?.contentDocument ?? null

export const dispatchEventToPreview = (eventName, detail) => {
  const event = new CustomEvent(eventName, { detail: detail })
  getPreviewIframeDoc().dispatchEvent(event)
}

export const getNodeInPreviewIframe = (nodeId) =>
  firstOutsideSlideClone(getPreviewIframeDoc(), `[data-node-id=${nodeId}]`)

export const getNodeInNodeTree = (nodeId) =>
  document.querySelector(`.node-tree[data-node-id=${nodeId}]`)

// getElementById against the preview document, tolerating both ways it can come up empty:
// there may be no preview iframe at all (getPreviewIframeDoc returns null), and an iframe
// that exists may simply not hold this node — it lives in a closed popup, in a global
// block, or the canvas hasn't re-rendered since it was added. The try/catch stays for the
// cross-origin case, where touching contentDocument can throw rather than return null.
export const getPreviewNodeByDomId = (previewDomId) => {
  try {
    return elementOutsideSlideClone(
      getPreviewIframeDoc()?.getElementById(previewDomId) ?? null
    )
  } catch {
    return null
  }
}

// Stimulus' dispatch() passes the event straight to target.dispatchEvent() with no check of
// its own, so a target that resolved to null throws — and the throw escapes into the caller,
// taking whatever came after it down too. Builder targets are routinely absent for the
// reasons above, and skipping is the right answer every time: the canvas, the node tree and
// the server reconcile on the next render regardless.
//
// The skip has to be written out rather than left to fall through, because Stimulus defaults
// an undefined target to controller.element — which would fire the event at the sidebar row
// instead of the canvas node it was meant for.
//
// Returns whether the event was dispatched, for callers that need to know.
export const dispatchToTarget = (
  controller,
  eventName,
  target,
  options = {}
) => {
  if (!target) return false

  controller.dispatch(eventName, { ...options, target })
  return true
}

// Shared timeout handle so overlapping suppress windows (page open, add-node)
// coalesce into one expiry instead of an earlier timer clearing a later one.
const HOVER_SCROLL_TIMEOUT = '__builderNodeTreeHoverScrollTimeout'

/**
 * Temporarily disable node-tree hover → canvas scroll.
 *
 * Used after editor open (a resting pointer over the right-hand node tree would
 * otherwise smooth-scroll the canvas toward mid/lower sections) and after adding
 * a node (so the tree doesn't fight the "scroll to new node" highlight).
 *
 * @param {number|null} [ms=2000] duration; pass `null` to hold until the next
 *   timed call (used while the preview loader is still spinning)
 * @param {Document} [doc=document] parent editor document (not the preview iframe)
 */
export function suppressNodeTreeHoverScroll(ms = 2000, doc = document) {
  const body = doc?.body
  if (!body) return

  body.dataset.disableNodeTreeHoverToScroll = 'true'
  // Schedule/clear only on `doc`'s window. Prefer calling this from the host editor
  // frame (see suppressNodeTreeHoverScrollFromIframe) so open/preview/add-node windows
  // always share one timer realm — iframe-created timers cannot be cleared by parent.
  const win = doc.defaultView || window
  if (win[HOVER_SCROLL_TIMEOUT]) {
    win.clearTimeout(win[HOVER_SCROLL_TIMEOUT])
    delete win[HOVER_SCROLL_TIMEOUT]
  }
  // Hold indefinitely while the canvas is still loading — a short timer started on
  // the first (not-yet-ready) previewLoaded would expire mid-load and re-enable
  // hover-scroll before the iframe finishes.
  if (ms == null) return

  win[HOVER_SCROLL_TIMEOUT] = win.setTimeout(() => {
    delete body.dataset.disableNodeTreeHoverToScroll
    delete win[HOVER_SCROLL_TIMEOUT]
  }, ms)
}

/* Will return true if the element passed is the last added node. False otherwise. It's assumed you are calling it from the preview iframe context (for now) */
export const isLastAddedNode = (element) => {
  const lastAddedNodeId = window.frameElement?.dataset?.lastAddedNodeId
  if (!lastAddedNodeId) return false

  const node = element.closest('.js-builder-node')
  const currentNodeId = node.dataset.nodeId

  return lastAddedNodeId === currentNodeId
}

// Same-origin readiness: true only when the iframe document can be scripted and is complete.
// Returns false when contentDocument is missing or cross-origin (callers should not script it).
export const iframeIsLoaded = (iframe, readyStateOverride = null) => {
  try {
    const doc = iframe?.contentDocument
    if (!doc) return false

    const url = doc.location.href
    const readyState = readyStateOverride || doc.readyState
    return readyState === 'complete' && !!url && url !== 'about:blank'
  } catch {
    return false
  }
}

// Preview spinner: hide when the iframe is ready, OR when its document is inaccessible
// after a load event (cross-origin / mid-navigation). Without this, the spinner sticks forever
// (SIMPLERO-12CC). Comparison mode got the try/catch in #24318; the main previewer did not.
export const shouldHidePreviewLoader = (iframe, readyStateOverride = null) => {
  try {
    if (!iframe?.contentDocument) {
      return readyStateOverride == null || readyStateOverride === 'complete'
    }
    return iframeIsLoaded(iframe, readyStateOverride)
  } catch {
    return readyStateOverride == null || readyStateOverride === 'complete'
  }
}

// The editor's chosen preview width, or '' at Fit. Lives in the breakpoint switcher's width box,
// which the node-editor header does not render -- callers must tolerate its absence.
export const getPreviewerWidth = () =>
  document.querySelector('input.js-builder-previewer-width')?.value

// Merges the preview width into a rails-ujs link's `params`, replacing any width already there
// rather than appending a second one. A blank width means Fit, and Fit is the absence of the
// param -- so it has to delete one set by an earlier click rather than leave it behind.
export const paramsWithPreviewerWidth = (existingParams, width) => {
  const params = new URLSearchParams(existingParams ?? '')

  if (width) params.set('previewer_width', width)
  else params.delete('previewer_width')

  return params.toString()
}

export const getCurrentBreakpoint = () => {
  const documentClassList = document.body.classList

  if (documentClassList.contains('mob-bp-active')) {
    return 'mob'
  } else if (documentClassList.contains('tab-bp-active')) {
    return 'tab'
  } else {
    return 'base'
  }
}
